import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ClipboardCheck, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface OutcomeRecord {
  id: string;
  decision_id: string;
  followup_due_at: string;
  followup_status: string;
  session_summary: string | null;
  decision_type: string | null;
}

interface OutcomeCheckSectionProps {
  workspaceId: string;
  userId: string;
}

export const OutcomeCheckSection = ({ workspaceId, userId }: OutcomeCheckSectionProps) => {
  const queryClient = useQueryClient();
  const [activeOutcome, setActiveOutcome] = useState<OutcomeRecord | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    decision_executed: '',
    outcome_direction: '',
    outcome_summary: '',
    would_decide_same: null as boolean | null,
  });

  const { data: dueOutcomes = [] } = useQuery({
    queryKey: ['outcome-checks', workspaceId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('decision_outcomes' as any) as any)
        .select('id, decision_id, followup_due_at, followup_status')
        .eq('workspace_id', workspaceId)
        .eq('followup_status', 'pending')
        .lte('followup_due_at', new Date().toISOString())
        .order('followup_due_at', { ascending: true })
        .limit(5);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Fetch session summaries
      const sessionIds = data.map((d: any) => d.decision_id);
      const { data: sessions } = await supabase
        .from('decision_sessions')
        .select('id, summary, decision_type')
        .in('id', sessionIds);

      const sessionMap = new Map((sessions || []).map(s => [s.id, s]));

      return data.map((o: any) => ({
        ...o,
        session_summary: sessionMap.get(o.decision_id)?.summary || null,
        decision_type: sessionMap.get(o.decision_id)?.decision_type || null,
      })) as OutcomeRecord[];
    },
    staleTime: 60_000,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!activeOutcome) return;
      await (supabase.from('decision_outcomes' as any) as any)
        .update({
          followup_status: 'answered',
          answered_at: new Date().toISOString(),
          decision_executed: answers.decision_executed,
          outcome_direction: answers.outcome_direction,
          outcome_summary: answers.outcome_summary || null,
          would_decide_same: answers.would_decide_same,
        })
        .eq('id', activeOutcome.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outcome-checks'] });
      setActiveOutcome(null);
      setStep(0);
      setAnswers({ decision_executed: '', outcome_direction: '', outcome_summary: '', would_decide_same: null });
    },
  });

  if (dueOutcomes.length === 0) return null;

  const steps = [
    {
      title: 'Wurde die Entscheidung umgesetzt?',
      field: 'decision_executed' as const,
      options: [
        { value: 'yes', label: 'Ja, vollständig' },
        { value: 'partially', label: 'Teilweise' },
        { value: 'no', label: 'Nein' },
      ],
    },
    {
      title: 'Hat sie in die richtige Richtung gewirkt?',
      field: 'outcome_direction' as const,
      options: [
        { value: 'worked', label: 'Ja, funktioniert' },
        { value: 'partly_worked', label: 'Teilweise' },
        { value: 'did_not_work', label: 'Nein' },
        { value: 'unknown', label: 'Noch unklar' },
      ],
    },
    {
      title: 'Würdest du die gleiche Entscheidung wieder treffen?',
      field: 'would_decide_same' as const,
      options: [
        { value: 'yes', label: 'Ja' },
        { value: 'no', label: 'Nein' },
      ],
    },
  ];

  return (
    <>
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Outcome-Check fällig</h3>
          <Badge variant="secondary" className="text-xs">{dueOutcomes.length}</Badge>
        </div>
        <div className="space-y-2">
          {dueOutcomes.map(outcome => (
            <button
              key={outcome.id}
              onClick={() => {
                setActiveOutcome(outcome);
                setStep(0);
              }}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-background hover:bg-muted/50 border border-border/50 transition-colors text-left"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {outcome.session_summary || 'Entscheidung'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Fällig seit {formatDistanceToNow(new Date(outcome.followup_due_at), { locale: de, addSuffix: true })}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!activeOutcome} onOpenChange={(open) => { if (!open) setActiveOutcome(null); }}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
          <SheetHeader className="pb-4 border-b border-border/50">
            <SheetTitle className="text-lg">Outcome-Check</SheetTitle>
            <SheetDescription className="text-sm">
              {activeOutcome?.session_summary || 'Entscheidung'}
            </SheetDescription>
          </SheetHeader>

          <div className="pt-6 px-1">
            {/* Progress dots */}
            <div className="flex items-center gap-2 mb-6 justify-center">
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    i < step ? 'bg-primary' : i === step ? 'bg-primary/60' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            {step < 3 ? (
              <div className="space-y-4">
                <h4 className="text-base font-medium text-foreground">{steps[step].title}</h4>
                <RadioGroup
                  value={
                    step === 2
                      ? (answers.would_decide_same === true ? 'yes' : answers.would_decide_same === false ? 'no' : '')
                      : (answers[steps[step].field as 'decision_executed' | 'outcome_direction'] || '')
                  }
                  onValueChange={(val) => {
                    if (step === 2) {
                      setAnswers(prev => ({ ...prev, would_decide_same: val === 'yes' }));
                    } else {
                      setAnswers(prev => ({ ...prev, [steps[step].field]: val }));
                    }
                  }}
                  className="space-y-2"
                >
                  {steps[step].options.map(opt => (
                    <div key={opt.value} className="flex items-center space-x-3 p-3 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors">
                      <RadioGroupItem value={opt.value} id={`outcome-${step}-${opt.value}`} />
                      <Label htmlFor={`outcome-${step}-${opt.value}`} className="text-sm cursor-pointer flex-1">
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <Button
                  className="w-full mt-4"
                  disabled={
                    step === 2
                      ? answers.would_decide_same === null
                      : !answers[steps[step].field as 'decision_executed' | 'outcome_direction']
                  }
                  onClick={() => setStep(s => s + 1)}
                >
                  Weiter <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <h4 className="text-base font-medium text-foreground">Zusammenfassung (optional)</h4>
                <Textarea
                  placeholder="Was war die wichtigste Erkenntnis?"
                  value={answers.outcome_summary}
                  onChange={(e) => setAnswers(prev => ({ ...prev, outcome_summary: e.target.value }))}
                  className="min-h-[80px]"
                />
                <Button
                  className="w-full"
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  Outcome speichern
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
